import { useEffect, useState, useRef } from 'react';
import { createMessenger } from '@userlike/messenger';

import { useSupportContext } from '@admin/shared/context/support';

/**
 * Extract widget key from URL.
 *
 * @param url - userlike widget url
 *
 * @returns {string}
 */
function getUserlikeWidgetKey(url) {
  const urlParts = url.split('/');
  const lastPart = urlParts[urlParts.length - 1];

  return lastPart?.split('.')[0] || '';
}

/**
 * Init userlike messenger API.
 *
 * @param widgetURL - userlike widget URL
 *
 * @returns {*}
 */
function useUserlikeMessenger(widgetURL) {
  // store messenger object
  const [api, setAPI] = useState(null);

  useEffect(() => {
    if (widgetURL == null) {
      return;
    }

    // get widget key from url
    const widgetKey = getUserlikeWidgetKey(widgetURL);
    if (widgetKey == null) {
      return;
    }

    // create userlike messenger
    createMessenger({
      version: 1,
      widgetKey,
    }).then((result) => {
      //store result in state
      // @ts-ignore - we know that api is in result
      const { api } = result.value;
      if (api != null) {
        setAPI(api);
      }
    });
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  // return current state
  return api;
}

/**
 * Userlike support component.
 *
 * This component initialized the userlike messenger.
 *
 * @returns {JSX.Element}
 */
export function UserlikeSupport(): JSX.Element {
  const { supportData, hideSupport } = useSupportContext();

  // store current size of messenger
  const chatIsMaximizedRef = useRef<boolean>(false);

  // get api
  const api = useUserlikeMessenger(supportData?.account);

  useEffect(() => {
    if (api == null) {
      return;
    }

    // mount messenger (makes it ready to use and puts it on the screen)
    api.mount().then(() => {
      // by default, the messenger is hidden, and will be changed to minimized,
      // and we have to wait until this is done to change the size again.
      api.state$.subscribe({
        next: ({ state }) => {
          // if state has changed to minimized
          // we need to check if it's the result of the initial mounting
          // process or if the user minimized the chat to hide it
          if (state === 'minimized') {
            if (chatIsMaximizedRef.current) {
              // if chat was maximized before, this is a user
              // interaction,
              // therefore we close the chat
              hideSupport();
            } else {
              // maximize chat window after mounting
              api.maximize().then(() => {
                // update flag
                chatIsMaximizedRef.current = true;
              });
            }
          }
        },
      });
    });

    // clean up
    // unmount messenger
    return () => {
      api.unmount();
    };
  }, [api]);

  return null;
}
